import React, { useEffect, useState, MouseEvent, useMemo } from 'react'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import Sites from '@toasttab/shared/src/data/Sites.json'
import { SiteSchema } from '@toasttab/shared/src/interfaces/Site.d'
import _helpers from '@toasttab/shared/src/globals/_helpers'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIcon } from '@toasttab/shared/src/assets/svg/icon-close.svg'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as IconWorkplaceEarth } from '@toasttab/shared/src/assets/svg/icon-workplace-earth.svg'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import {
  formatUrl,
  getRegionCode,
  getRegionLabel,
  groupSitesByContinent,
  normalizeLang,
  normalizeRegion
} from '@toasttab/shared/src/utils/siteUtils'

const normalizeCountryCode = (lang = '') =>
  lang.toLowerCase().split('-')[1] || lang.toLowerCase()
interface RegionModalProps {
  packageName: string
  siteId: number
  localizedEntries?: {
    siteId: number
    language: string
    slug?: string
    slugOverride?: string
    disabledRegionalSwitcherModal?: boolean
  }[]
}

const RegionSwitcher: React.FC<RegionModalProps> = ({
  packageName,
  siteId,
  localizedEntries = []
}) => {
  const { t } = useI18next()
  const { isDataPage } = usePageContext()
  const [regionData, setRegionData] = useState({
    countryCode: '',
    regionPreference: ''
  })
  const { geoData, loading } = useGeoLocation()
  const [suggested, setSuggested] = useState({
    name: '',
    url: '',
    language: ''
  })
  const [languageMismatchModal, setLanguageMismatchModal] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)

  const browserLang = useMemo(
    () =>
      typeof navigator === 'undefined'
        ? 'en'
        : navigator.language?.split('-')[0]?.toLowerCase() || 'en',
    []
  )

  useEffect(() => {
    if (geoData) {
      setRegionData({
        countryCode: geoData.country.iso_code,
        regionPreference: geoData.regionPreference.iso_code
      })
    }
  }, [geoData, loading])

  useEffect(() => {
    const userLangCookie = _helpers.getCookie('_userLanguage')
    if (!userLangCookie && browserLang) {
      _helpers.createCookie('_userLanguage', browserLang, 365, '/')
    }
  }, [browserLang])

  const activeSite = useMemo(
    () => Sites.sites.find((site) => site.siteId === siteId),
    [siteId]
  )
  const pkg = useMemo(
    () => Sites.packages.find((p) => p.package === packageName),
    [packageName]
  )

  const getPackagePath = () => {
    if (isDataPage) return '/blog/data'
    return pkg?.path || ''
  }

  // get Suggested Site
  useEffect(() => {
    if (!regionData.countryCode || !activeSite || !pkg) return

    const userLang =
      _helpers.getCookie('_userLanguage')?.toLowerCase() || browserLang
    const preferredRegion = normalizeRegion(
      _helpers.getCookie('_preferredRegion')
    )
    const userCountry = normalizeRegion(regionData.countryCode)

    // Step 1: Find a suggested site by preferredRegion + userLang first
    let suggestedSite = Sites.sites.find(
      (site) =>
        preferredRegion &&
        normalizeRegion(site.countryCode) === preferredRegion &&
        site.language.toLowerCase().startsWith(userLang)
    )

    // Step 1b: If not found, fall back to just region matching
    if (!suggestedSite && preferredRegion) {
      suggestedSite = Sites.sites.find(
        (site) => normalizeRegion(site.countryCode) === preferredRegion
      )
    }

    // Step 2: If not found, try matching userLang + userCountry
    if (!suggestedSite) {
      suggestedSite = Sites.sites.find(
        (site) =>
          normalizeRegion(site.countryCode) === userCountry &&
          site.language.toLowerCase().startsWith(userLang)
      )
    }

    // Step 3: Still nothing? Match just region priority: preferredRegion -> userCountry
    if (!suggestedSite) {
      suggestedSite = Sites.sites.find(
        (site) =>
          normalizeRegion(site.countryCode) === preferredRegion ||
          normalizeRegion(site.countryCode) === userCountry
      )
    }

    if (!suggestedSite) return

    const entry = localizedEntries.find(
      (e) => e.language === suggestedSite.language
    )

    // If page doesn't exist in target language, redirect to homepage
    let url = suggestedSite.url || '/'

    if (entry) {
      // Page exists in target language, use the specific slug
      const slug = entry.slugOverride || entry.slug || ''
      url = formatUrl(suggestedSite, getPackagePath(), slug)
    }
    // If no entry exists, keep url as homepage

    setSuggested((prev) =>
      prev.name === suggestedSite.languageName &&
      prev.url === url &&
      prev.language === suggestedSite.language
        ? prev
        : {
            name: suggestedSite.languageName,
            url,
            language: suggestedSite.language
          }
    )
  }, [regionData, activeSite, pkg, localizedEntries, browserLang])

  const shouldShowModal = useMemo(() => {
    const userLangCookie = _helpers.getCookie('_userLanguage')
    const userCountry = regionData.countryCode?.toUpperCase()
    const preferredRegion = regionData.regionPreference?.toUpperCase()
    const currentSiteRegion = getRegionCode(
      activeSite?.contentfulRegionCode
    )?.toUpperCase()

    const doesRegionHaveSupportedContent = localizedEntries.some((entry: any) =>
      Sites.sites.some(
        (site) =>
          site.siteId === entry.siteId &&
          normalizeRegion(site.countryCode) ===
            normalizeRegion(preferredRegion || userCountry)
      )
    )

    const doesLanguageHaveSupportedContent = localizedEntries.some(
      (entry: any) =>
        normalizeLang(entry.language) === normalizeLang(userLangCookie) &&
        (normalizeCountryCode(entry.language) ===
          normalizeCountryCode(userLangCookie) ||
          normalizeCountryCode(entry.language) ===
            normalizeCountryCode(userCountry))
    )

    return (
      doesRegionHaveSupportedContent &&
      doesLanguageHaveSupportedContent &&
      ((preferredRegion && currentSiteRegion !== preferredRegion) ||
        (!preferredRegion && currentSiteRegion !== userCountry) ||
        (!preferredRegion && !userCountry))
    )
  }, [regionData, activeSite, localizedEntries])

  // identify the language mismatched
  useEffect(() => {
    const userLangCookie = _helpers.getCookie('_userLanguage')
    if (!userLangCookie && browserLang) {
      _helpers.createCookie('_userLanguage', browserLang, 365, '/')
    }
    setLanguageMismatchModal(false)
    const isUserLangSupported = localizedEntries.some(
      (entry: any) =>
        normalizeLang(entry.language) === normalizeLang(userLangCookie)
    )
    const preferredRegion = normalizeRegion(
      _helpers.getCookie('_preferredRegion')
    )
    const currentSiteRegion = normalizeRegion(activeSite?.contentfulRegionCode)
    const skipLanguageModal =
      preferredRegion &&
      preferredRegion === currentSiteRegion &&
      normalizeLang(activeSite?.language) === normalizeLang(userLangCookie)

    if (
      !isDismissed &&
      !shouldShowModal &&
      !skipLanguageModal &&
      userLangCookie &&
      activeSite?.language &&
      normalizeLang(activeSite?.language) !== normalizeLang(userLangCookie) &&
      isUserLangSupported
    ) {
      setLanguageMismatchModal(true)
    }
  }, [
    browserLang,
    regionData,
    activeSite,
    shouldShowModal,
    isDismissed,
    localizedEntries
  ])

  const showContentNotFound = useMemo(() => {
    const isPageNotFound = localizedEntries.some(
      (entry) => entry.disabledRegionalSwitcherModal
    )
    return isPageNotFound
  }, [localizedEntries])

  const handleSubmit = () => {
    const regionSlug = suggested.url.split('/')[1]?.toUpperCase()
    const region = getRegionCode(regionSlug)
    _helpers.createCookie('_preferredRegion', region, 30, '/')
    _helpers.createCookie('_userLanguage', suggested.language, 365, '/')
    window.location.assign(suggested.url)
  }

  const handleAlternatedLinkSubmit = (site: SiteSchema) => {
    _helpers.createCookie(
      '_preferredRegion',
      getRegionCode(site.contentfulRegionCode),
      30,
      '/'
    )
    _helpers.createCookie('_userLanguage', site.language, 365, '/')
    window.location.href = site.url || '/'
  }

  const handleClose = () => {
    const region = getRegionCode(activeSite?.contentfulRegionCode || 'US')
    _helpers.createCookie('_preferredRegion', region, 30, '/')
    _helpers.createCookie(
      '_userLanguage',
      activeSite?.language || 'en',
      365,
      '/'
    )
    setIsDismissed(true)
  }

  const stopPropagation = (e: MouseEvent<HTMLDivElement>) => e.stopPropagation()
  const readyToRender =
    regionData.countryCode &&
    activeSite &&
    pkg &&
    ((shouldShowModal && !isDismissed) || languageMismatchModal)
  if (!readyToRender) return null

  const groupedSites = groupSitesByContinent(
    Sites.sites,
    suggested.language,
    localizedEntries,
    activeSite?.language,
    getPackagePath()
  )

  return (
    <div
      className='fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-30 p-4'
      data-testid='MainRegionSelector-modal'
      id='regionSelect-modal'
      onClick={handleClose}
    >
      <div
        className='relative w-full max-w-[550px] max-h-[90vh] bg-white rounded-lg shadow-xl overflow-y-auto'
        onClick={stopPropagation}
      >
        <button className='absolute top-1 right-1' onClick={handleClose}>
          <CloseIcon height='30' width='30' />
        </button>
        <nav aria-label='Switch language' className='p-10'>
          <IconWorkplaceEarth height='28' width='28' />
          <h4 className='text-2xl font-effra font-normal mb-6'>
            {showContentNotFound
              ? t('Sorry, this content is not available in your region', {
                  defaultValue:
                    'Sorry, this content is not available in your region'
                })
              : t('For the best experience, view our site in your region', {
                  defaultValue:
                    'For the best experience, view our site in your region.'
                })}
          </h4>
          <div className='mb-5 border-b'>
            {showContentNotFound ? (
              <Button
                classes='py-3 px-6 mb-14'
                onClick={() => (window.location.href = activeSite?.url || '/')}
                variation='primary'
              >
                {t('Back to Home')}
              </Button>
            ) : (
              <Button
                classes='w-full md:w-auto py-3 px-6 mb-6'
                fontWeight={['font-semibold']}
                numComponents={1}
                onClick={handleSubmit}
                variation='primary'
              >
                {t(
                  getRegionLabel(
                    suggested.name,
                    suggested.language.split('-')[1],
                    activeSite?.contentfulRegionCode
                  )
                )}
              </Button>
            )}
            {!showContentNotFound && (
              <div className='mb-14'>
                <button
                  className='text-left hover:underline text-blue-75'
                  onClick={() => handleClose()}
                >
                  {t('Continue to', {
                    region: t(
                      getRegionLabel(
                        activeSite.languageName,
                        activeSite.contentfulRegionCode,
                        suggested.language.split('-')[1]
                      )
                    )
                  })}
                </button>
              </div>
            )}
          </div>
          <h5 className='text-2xl font-sansPro font-semibold mb-6'>
            {t('Or choose a different region...')}
          </h5>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            {Object.entries(groupedSites).map(([continent, countries]) => (
              <div key={continent}>
                <h3 className='uppercase text-sm mb-2'>{t(continent)}</h3>
                <ul>
                  {countries.map((site) => (
                    <li className='mb-2' key={`site-${site.siteId}`}>
                      <button
                        className='text-left hover:underline text-blue-75'
                        onClick={() => handleAlternatedLinkSubmit(site)}
                      >
                        {t((site as any).languageName)}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </nav>
      </div>
    </div>
  )
}

export default RegionSwitcher
